import React, { useState, useEffect } from "react";
import "./index.css";
import { NameCard } from "./NameCard";
import { linksService } from "../../../apis/Business/ActionPlanLinksApi";
import plusIcon from "../../../assets/shapes_svg/ambition/Add_link_icon.svg";
import asteriskIcon from "../../../assets/shapes_svg/ambition/Asteriks_point_2.svg";
import { useUserAccess } from "../../../components/WriteAccessContext";

export const LinksSection = ({ effortId }) => {
  const [links, setLinks] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [newLink, setNewLink] = useState({ label: "Intranet", links: "" });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { userAccess } = useUserAccess();

  // Link type options
  const linkTypes = ["Intranet", "External link", "YFS link"];

  useEffect(() => {
    if (effortId) {
      fetchLinks();
    }
  }, [effortId]);

  const fetchLinks = async () => {
    try {
      setIsLoading(true);
      const fetchedLinks = await linksService.getLinks(effortId);
      setLinks(fetchedLinks);
      setError(null);
    } catch (err) {
      setError("Failed to fetch links. Please try again later.");
      console.error("Error fetching links:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const addLink = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newLink.label || !newLink.links) {
      setError("Please fill in both fields");
      return;
    }

    try {
      setIsLoading(true);
      const createdLink = await linksService.createLink(effortId, newLink);
      setLinks([...links, createdLink]);
      setNewLink({ label: "Intranet", links: "" });
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError("Failed to create link. Please try again.");
      console.error("Error creating link:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (linkId) => {
    try {
      setIsLoading(true);
      await linksService.deleteLink(effortId, linkId);
      setLinks(links.filter((link) => link.id !== linkId));
      setError(null);
    } catch (err) {
      setError("Failed to delete link. Please try again.");
      console.error("Error deleting link:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewLink({ ...newLink, [name]: value });
    setError(null);
  };

  if (isLoading && !links.length) {
    return (
      <div className="links-section">
        <NameCard
          deptName="Links"
          isBottom={false}
          className="other-effort-container"
        />
      </div>
    );
  }

  return (
    <div className="links-section">
      <NameCard
        deptName="Links"
        isBottom={false}
        className="other-effort-container"
      />

      <div className="links-list action-plans-parent-bubble">
        {links.map((link) => (
          <div key={link.id} className="link-item">
            <img
              src={asteriskIcon}
              alt="Asterisk Icon"
              width={25}
              height={25}
            />
            <a
              href={link.links}
              target="_blank"
              rel="noopener noreferrer"
              className="link-text"
            >
              {link.label} - {link.links}
            </a>
            <button
              className="delete-link-button"
              onClick={() => handleDelete(link.id)}
              aria-label="Delete link"
              disabled={userAccess !== "write"}
            >
              ×
            </button>
          </div>
        ))}

        {isEditing && userAccess === "write" && (
          <form onSubmit={handleSubmit} className="new-link-form">
            <select
              name="label"
              value={newLink.label}
              onChange={handleChange}
              className="link-input"
              disabled={isLoading}
            >
              {linkTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <input
              type="url"
              name="links"
              value={newLink.links}
              onChange={handleChange}
              placeholder="URL"
              className="link-input"
              disabled={isLoading}
            />
            <button
              type="submit"
              className="submit-link-button"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
          </form>
        )}

        {!isEditing && (
          <button
            className="add-link-button"
            onClick={addLink}
            disabled={isLoading || userAccess !== "write"}
          >
            <img src={plusIcon} alt="Plus Icon" width={20} height={20} />
          </button>
        )}

        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default LinksSection;
